import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  OnDestroy,
  output,
  signal,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import SignaturePad from 'signature_pad';
import { Button } from '@openng/optimus-ui/button';
import { TranslocoDirective } from '@jsverse/transloco';

@Component({
  selector: 'lib-signature-pad',
  imports: [Button, TranslocoDirective],
  templateUrl: './signature-pad.component.html',
  styleUrl: './signature-pad.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SignaturePadComponent)
    }
  ]
})
export class SignaturePadComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @ViewChild('canvas') canvasRef: ElementRef<HTMLCanvasElement>;

  readonly signed = output<string>();
  readonly disabled = signal<boolean>(false);

  private pad: SignaturePad | null = null;
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.resizeCanvas(canvas);
    this.pad = new SignaturePad(canvas);
    this.pad.addEventListener('endStroke', () => {
      const dataUrl = this.getDataUrl();
      this.onChange(dataUrl);
      this.signed.emit(dataUrl);
    });
  }

  private resizeCanvas(canvas: HTMLCanvasElement): void {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')!.scale(ratio, ratio);
  }

  clear(): void {
    this.pad?.clear();
    this.onChange(null);
    this.onTouched();
  }

  isEmpty(): boolean {
    return this.pad?.isEmpty() ?? true;
  }

  getDataUrl(): string {
    return this.canvasRef.nativeElement.toDataURL('image/png');
  }

  writeValue(value: string | null): void {
    if (!this.pad) {
      return;
    }

    if (value) {
      void this.pad.fromDataURL(value);
    } else {
      this.pad.clear();
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.pad?.off();
    } else {
      this.pad?.on();
    }
  }

  ngOnDestroy(): void {
    this.pad?.off();
  }
}
